import { EWorksheetStatus } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormValues,
  buildBaseWorksheetFormInitialValues,
  StoredTermAndPlan,
  useBaseWorksheetFormFields,
  useBaseWorksheetFormReady,
} from '@src/components/WorksheetForm'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { EasyCareWorksheet, EditEasyCareWorksheetDto } from '@src/data/types/EasyCareWorksheetSchema'
import { useMemo } from 'react'

export const VEHICLE_ASSISTANCE_TERMS = [12, 24, 36]

export type EasyCareEditValues = BaseWorksheetFormValues & {
  includeVehicleAssistance: boolean
  includeLoanProtection: boolean
  vehicleAssistanceTerm: number | null
}

export type EasyCareEditSchema = ReturnType<typeof useEasyCareEditSchema>

export type EasyCareEditSubmitValues = yup.InferSubmit<EasyCareEditSchema>

export const useEasyCareEditSchema = (config: BaseWorksheetFormConfig) => {
  const fields = useBaseWorksheetFormFields(config)

  return useMemo(
    () =>
      yup.object({
        amountRequested: fields.amountRequested,
        paymentPlanId: fields.paymentPlanId,
        term: fields.term,
        paymentFrequency: fields.paymentFrequency,
        activationDate: fields.activationDate,
        firstPaymentDate: fields.firstPaymentDate,
        includeProtection: fields.includeProtection,
        isHighEstimate: fields.isHighEstimate,
        includeVehicleAssistance: yup.boolean().required().default(false),
        includeLoanProtection: yup.boolean().required().default(false),
        vehicleAssistanceTerm: yup
          .number()
          .nullable()
          .when('includeVehicleAssistance', {
            is: true,
            then: (schema) => schema.required().oneOf(VEHICLE_ASSISTANCE_TERMS),
          })
          .default(() => null),
      }),
    [fields],
  )
}

export const fromWorksheet = (defaults: EasyCareEditValues, worksheet: EasyCareWorksheet): EasyCareEditValues => ({
  ...buildBaseWorksheetFormInitialValues(defaults, worksheet as BaseWorksheetDto, null),
  includeVehicleAssistance: worksheet.includeVehicleAssistance ?? defaults.includeVehicleAssistance,
  includeLoanProtection: worksheet.includeLoanProtection ?? defaults.includeLoanProtection,
  vehicleAssistanceTerm: worksheet.vehicleAssistanceTerm ?? defaults.vehicleAssistanceTerm,
})

export const useEasyCareEditForm = (config: BaseWorksheetFormConfig, worksheet: EasyCareWorksheet) => {
  const schema = useEasyCareEditSchema(config)
  const isReady = useBaseWorksheetFormReady(config)

  const defaultValues = useMemo<EasyCareEditValues | undefined>(() => {
    if (!isReady) return undefined
    return fromWorksheet(schema.getDefault() as EasyCareEditValues, worksheet)
  }, [isReady, schema, worksheet])

  return { schema, defaultValues, isReady }
}

export const getStoredTermAndPlan = (worksheet: EasyCareWorksheet): StoredTermAndPlan | null =>
  worksheet.paymentPlanId && worksheet.term ? { paymentPlanId: worksheet.paymentPlanId, term: worksheet.term } : null

export const toEditEasyCareWorksheetDto = (
  values: EasyCareEditSubmitValues,
  worksheet: EasyCareWorksheet,
): EditEasyCareWorksheetDto => ({
  creditApplicationId: worksheet.creditApplicationId,
  versionTag: worksheet.versionTag,
  status: EWorksheetStatus.Active,
  includeVehicleAssistance: values.includeVehicleAssistance,
  includeLoanProtection: values.includeLoanProtection,
  vehicleAssistanceTerm: values.includeVehicleAssistance ? values.vehicleAssistanceTerm : null,
  paymentFrequency: values.paymentFrequency,
  deliveryOn: values.activationDate.toString(),
  firstPaymentOn: values.firstPaymentDate.toString(),
})
